export const CHECKOUT_RETURN_PATH_KEY = "vf_checkout_return_path";

type CheckoutReturnStorage = Pick<Storage, "getItem" | "setItem">;
type CheckoutHistoryState = { back?: unknown } | null;

const INTERNAL_ORIGIN = "https://storefront.invalid";

const currentSessionStorage = (): CheckoutReturnStorage | null => {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const isUnavailableReturnPath = (pathname: string) => {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return (
    normalized === "/store/cart" ||
    normalized === "/store/checkout" ||
    normalized.startsWith("/store/checkout/")
  );
};

export const normalizeCheckoutReturnPath = (candidate: unknown) => {
  if (typeof candidate !== "string") return null;

  const value = candidate.trim();
  if (!value.startsWith("/") || value.startsWith("//")) return null;

  try {
    const url = new URL(value, INTERNAL_ORIGIN);
    if (
      url.origin !== INTERNAL_ORIGIN ||
      isUnavailableReturnPath(url.pathname)
    ) {
      return null;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

export const rememberCheckoutReturnPath = (
  candidate: unknown,
  storage: CheckoutReturnStorage | null = currentSessionStorage()
) => {
  const path = normalizeCheckoutReturnPath(candidate);
  if (!path || !storage) return false;

  try {
    storage.setItem(CHECKOUT_RETURN_PATH_KEY, path);
    return true;
  } catch {
    return false;
  }
};

export const getCheckoutReturnPath = (
  storage: CheckoutReturnStorage | null = currentSessionStorage()
) => {
  if (!storage) return "/";

  try {
    return (
      normalizeCheckoutReturnPath(storage.getItem(CHECKOUT_RETURN_PATH_KEY)) ||
      "/"
    );
  } catch {
    return "/";
  }
};

const currentHistoryState = (): CheckoutHistoryState => {
  if (typeof window === "undefined") return null;

  try {
    return window.history.state as CheckoutHistoryState;
  } catch {
    return null;
  }
};

export const shouldUseCheckoutHistoryBack = (
  returnPath: unknown,
  historyState: CheckoutHistoryState = currentHistoryState()
) => {
  const normalizedReturnPath = normalizeCheckoutReturnPath(returnPath);
  const normalizedBackPath = normalizeCheckoutReturnPath(historyState?.back);

  return !!normalizedReturnPath && normalizedBackPath === normalizedReturnPath;
};
